import React, { useState, useEffect, useCallback, useRef } from 'react'; // Added useRef
import axios from 'axios';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import moment from 'moment-timezone'; // Import moment-timezone
import { getFormattedTimezones } from '../../utils/timezoneUtils'; // Import timezone utility
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  FormControl, // Added
  InputLabel,  // Added
  Select,      // Added
  MenuItem,    // Added
  FormGroup,   // Added for Services section
  FormControlLabel, // Added for Services section
  Checkbox,    // Added for Services section
  Alert,       // Added for error display
  Snackbar     // Added for error popup
} from '@mui/material';
import { MyLocation as MyLocationIcon } from '@mui/icons-material';
import { useNotification } from '../../context/NotificationContext.js';
// Removed GoogleMapReact import
// Import Leaflet
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
import { initializeMap, addMarker, setMapView, fixDefaultIconPaths, updateMarkerPosition } from '../../utils/mapUtils'; // Import Leaflet utils
import { useUser } from '../../context/UserContext'; // Import useUser hook
import { useOrg } from '../../context/OrgContext';

// Removed GoogleMapReact specific MapMarker component

function CreateFacilityDialog({ open, onClose, onFacilityCreated }) {
  const { currentUser: userProfile } = useUser(); // Use useUser hook, alias currentUser
  const { name: orgName } = useOrg();
  const { showNotification } = useNotification();
  const [newFacility, setNewFacility] = useState({
    facilityName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    mcNumber: '', // Added MC Number
    dotNumber: '', // Added DOT Number
    timezone: moment.tz.guess(), // Default to browser guess
    lat: 39.8283, // Default map center (US)
    lng: -98.5795,
    // Schedule object might need default values if required by backend
    schedule: {},
    // Services object for facility capabilities
    services: {},
    vendorNumber: '' // Add vendorNumber to state
    // Removed incorrect applicationId logic
  });
  const [spUsers, setSpUsers] = useState([]); // State to store SP users for the dropdown
  const [mapCoordinates, setMapCoordinates] = useState({ lat: 39.8283, lng: -98.5795 }); // Keep this for marker state
  // Removed mapKey, mapLoading, mapError states
  const [loading, setLoading] = useState(false);
  const mapContainerRef = useRef(null); // Ref for map container
  const mapRef = useRef(null); // Ref for map instance
  const markerRef = useRef(null); // Ref for marker instance
  const [mapInitialized, setMapInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

   // Helper function to get auth headers
   const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  // Effect to reset state and initialize map when dialog opens
  useEffect(() => {
    if (open) {
      // Reset form state
      setNewFacility({
          facilityName: '', address1: '', address2: '', city: '', state: '', zip: '',
          contactName: '', contactPhone: '', contactEmail: '', mcNumber: '', dotNumber: '',
          timezone: moment.tz.guess(),
          lat: 39.8283, lng: -98.5795, schedule: {}, services: {}, vendorNumber: '' // Reset vendorNumber
      });
      setMapCoordinates({ lat: 39.8283, lng: -98.5795 });
      setError(null);
      setSnackbarOpen(false);
      setMapInitialized(false); // Ensure map re-initializes if needed

      // Initialize map after a short delay to ensure container is rendered
      const timer = setTimeout(() => {
        if (mapContainerRef.current && !mapRef.current) {
          fixDefaultIconPaths();
          const mapInstance = initializeMap(mapContainerRef.current, {
            center: [mapCoordinates.lat, mapCoordinates.lng],
            zoom: 10,
          });
          mapInstance.on('click', handleMapClick); // Add click listener
          mapRef.current = mapInstance;
          setMapInitialized(true);
          // Add initial marker
          markerRef.current = addMarker(mapInstance, [mapCoordinates.lat, mapCoordinates.lng]);
        }
      }, 100); // 100ms delay

      return () => {
        clearTimeout(timer);
        // Cleanup map on dialog close
        if (mapRef.current) {
          mapRef.current.off('click', handleMapClick);
          mapRef.current.remove();
          mapRef.current = null;
          markerRef.current = null;
        }
      };
    }
  }, [open]); // Only run when 'open' state changes

  // Effect to fetch SP users when the dialog opens
  useEffect(() => {
    if (open) {
      const fetchSpUsers = async () => {
        const headers = getAuthHeader();
        if (!headers) return;
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users?role=SP&fetchForAllVendors=true`, { headers });
          const sps = response.data && Array.isArray(response.data.records) ? response.data.records : [];

          // Include the Org 1 HQ vendor so owner facilities can be created
          // here too (SP list alone never contains the owner account)
          try {
            const me = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`, { headers });
            const owner = me.data;
            if (owner && ['OW', 'sOW', 'OWp'].includes(owner.primaryRole) && (owner.vendorNumber || owner.vendorId)) {
              const ownerVendor = owner.vendorNumber || owner.vendorId;
              if (!sps.some((u) => u.vendorNumber === ownerVendor)) {
                sps.unshift({
                  vendorNumber: ownerVendor,
                  companyName: `${owner.companyName || orgName} (HQ)`
                });
              }
            }
          } catch { /* owner lookup is best-effort; SP list still renders */ }

          setSpUsers(sps);
        } catch (error) {
          console.error("Error fetching SP users:", error);
          const errorMessage = error.response?.data?.message || error.message || 'Failed to load SP users.';
          showNotification(`Error: ${errorMessage}`, 'error');
        }
      };
      fetchSpUsers();
    } else {
       setSpUsers([]); // Clear SP users when dialog closes
    }
  }, [open, getAuthHeader, orgName]); // Rerun when dialog opens or auth header changes

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    // Phone fields accept any format and normalize to (XXX) XXX-XXXX
    const nextValue = name === 'contactPhone' ? formatPhoneNumber(value) : value;
    setNewFacility(prev => ({ ...prev, [name]: nextValue }));
  };

  // Handle vendor selection change
  const handleVendorChange = (event) => {
    setNewFacility(prev => ({ ...prev, vendorNumber: event.target.value }));
  };

  const handleDetectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newCoords = { lat: position.coords.latitude, lng: position.coords.longitude };
          setMapCoordinates(newCoords);
          setNewFacility(prev => ({ ...prev, lat: newCoords.lat, lng: newCoords.lng }));
        },
        () => showNotification('Geolocation not supported or permission denied', 'warning')
      );
    } else {
      showNotification('Geolocation not supported by this browser', 'warning');
    }
  };

  // Updated map click handler for Leaflet
  const handleMapClick = useCallback((e) => {
    const { lat, lng } = e.latlng;
    setMapCoordinates({ lat, lng });
    setNewFacility(prev => ({ ...prev, lat: lat, lng: lng }));
    // Update marker position
    if (mapRef.current && markerRef.current) {
      updateMarkerPosition(markerRef.current, [lat, lng]);
      setMapView(mapRef.current, [lat, lng], mapRef.current.getZoom()); // Optionally center map
    } else if (mapRef.current) {
        // Add marker if it doesn't exist (shouldn't happen with current init logic, but safe)
        markerRef.current = addMarker(mapRef.current, [lat, lng]);
    }
  }, []); // No dependencies needed if it only uses refs and setters

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCreateFacility = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    // Basic Validation
    if (!newFacility.facilityName || !newFacility.address1 || !newFacility.city || !newFacility.state || !newFacility.zip) {
        setError("Please fill in all required address fields (Name, Address 1, City, State, Zip).");
        setSnackbarOpen(true);
        return; // Stops execution if validation fails
    }
    // Removed incorrect applicationId validation

    // Basic Validation for vendor selection
    if (!newFacility.vendorNumber) {
        setError("Please select a Vendor.");
        setSnackbarOpen(true);
        return;
    }

    setLoading(true);
    setError(null);
    try {
      // The payload already includes vendorNumber from newFacility state
      const { mcNumber, dotNumber, ...restOfFacility } = newFacility; // Destructure to ensure they are included
      const payload = {
        ...restOfFacility,
        mcNumber,
        dotNumber,
        // The backend will handle the region check based on the user's role and the facility's state
      };
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/facilities`, payload, { headers });
      showNotification('Facility created successfully!', 'success');
      onFacilityCreated(response.data); // Pass the newly created facility back
      onClose(); // Close dialog on success
    } catch (err) {
      console.error("Error creating facility:", err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to create facility.";
      setError(errorMessage);
      setSnackbarOpen(true);
      showNotification(`Error creating facility: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Site (Facility)</DialogTitle>
      <DialogContent>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
        <TextField margin="dense" label="Facility Name *" name="facilityName" fullWidth required value={newFacility.facilityName} onChange={handleInputChange} />
        <TextField margin="dense" label="Address 1 *" name="address1" fullWidth required value={newFacility.address1} onChange={handleInputChange} />
        <TextField margin="dense" label="Address 2" name="address2" fullWidth value={newFacility.address2} onChange={handleInputChange} />
        <TextField margin="dense" label="City *" name="city" fullWidth required value={newFacility.city} onChange={handleInputChange} />
        <TextField margin="dense" label="State *" name="state" fullWidth required value={newFacility.state} onChange={handleInputChange} />
        <TextField margin="dense" label="Zip *" name="zip" fullWidth required value={newFacility.zip} onChange={handleInputChange} />
        <TextField margin="dense" label="Contact Name" name="contactName" fullWidth value={newFacility.contactName} onChange={handleInputChange} />
        <TextField margin="dense" label="Contact Phone" name="contactPhone" fullWidth value={newFacility.contactPhone} onChange={handleInputChange} />
        <TextField margin="dense" label="Contact Email" name="contactEmail" type="email" fullWidth value={newFacility.contactEmail} onChange={handleInputChange} />
        <TextField margin="dense" label="MC Number" name="mcNumber" fullWidth value={newFacility.mcNumber} onChange={handleInputChange} />
        <TextField margin="dense" label="DOT Number" name="dotNumber" fullWidth value={newFacility.dotNumber} onChange={handleInputChange} />
 
        {/* For Vendor Dropdown */}
        <FormControl fullWidth margin="dense" required>
          <InputLabel id="vendor-select-label">For Vendor *</InputLabel>
          <Select
            labelId="vendor-select-label"
            id="vendor-select"
            name="vendorNumber" // Use name "vendorNumber" to match state field
            value={newFacility.vendorNumber}
            label="For Vendor *"
            onChange={handleVendorChange} // Use the new handler
          >
            <MenuItem value=""><em>Select a Vendor</em></MenuItem>
            {spUsers.map((user) => (
              <MenuItem key={user.vendorNumber} value={user.vendorNumber}>
                {`${user.companyName} (${user.vendorNumber})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Timezone Selector */}
        <FormControl fullWidth margin="dense" required>
          <InputLabel id="timezone-select-label">Timezone *</InputLabel>
          <Select
            labelId="timezone-select-label"
            id="timezone-select"
            name="timezone"
            value={newFacility.timezone}
            label="Timezone *"
            onChange={handleInputChange}
          >
            {getFormattedTimezones().map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Map Interaction */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>Set Location (Optional)</Typography>
        <Box mt={1} style={{ height: '300px', border: '1px solid #ccc', position: 'relative' }} ref={mapContainerRef}>
          {/* Leaflet map will be initialized here */}
          {!mapInitialized && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Initializing Map...</Typography>
            </Box>
          )}
        </Box>
        <Typography variant="caption">Click map to set coordinates (Lat: {mapCoordinates.lat.toFixed(4)}, Lng: {mapCoordinates.lng.toFixed(4)})</Typography>
        <Button startIcon={<MyLocationIcon />} onClick={handleDetectLocation} sx={{ ml: 1 }}>Detect My Location</Button>

        {/* Services Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>Services</Typography>
        <Box sx={{ mt: 1 }}>
          
          {/* Roadside Services */}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Roadside Services</Typography>
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              {/* Jump Starts */}
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Jump Starts</Typography>
              <Box sx={{ ml: 2 }}>
                {['roadsideJumpStartsLightDuty', 'roadsideJumpStartsMediumDuty', 'roadsideJumpStartsHeavyDuty'].map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={newFacility.services?.[key] || false}
                        onChange={(e) => {
                          setNewFacility({
                            ...newFacility,
                            services: {
                              ...newFacility.services,
                              [key]: e.target.checked
                            }
                          });
                        }}
                      />
                    }
                    label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                  />
                ))}
              </Box>

              {/* Tire Changes */}
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Tire Changes</Typography>
              <Box sx={{ ml: 2 }}>
                {['roadsideTireChangesLightDuty', 'roadsideTireChangesMediumDuty', 'roadsideTireChangesHeavyDuty'].map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={newFacility.services?.[key] || false}
                        onChange={(e) => {
                          setNewFacility({
                            ...newFacility,
                            services: {
                              ...newFacility.services,
                              [key]: e.target.checked
                            }
                          });
                        }}
                      />
                    }
                    label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                  />
                ))}
              </Box>

              {/* Lock Outs */}
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Lock Outs</Typography>
              <Box sx={{ ml: 2 }}>
                {['roadsideLockOutsLightDuty', 'roadsideLockOutsMediumDuty', 'roadsideLockOutsHeavyDuty'].map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={newFacility.services?.[key] || false}
                        onChange={(e) => {
                          setNewFacility({
                            ...newFacility,
                            services: {
                              ...newFacility.services,
                              [key]: e.target.checked
                            }
                          });
                        }}
                      />
                    }
                    label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                  />
                ))}
              </Box>

              {/* Fuel Deliveries */}
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Fuel Deliveries</Typography>
              <Box sx={{ ml: 2 }}>
                {['roadsideFuelDeliveriesLightDuty', 'roadsideFuelDeliveriesMediumDuty', 'roadsideFuelDeliveriesHeavyDuty'].map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={newFacility.services?.[key] || false}
                        onChange={(e) => {
                          setNewFacility({
                            ...newFacility,
                            services: {
                              ...newFacility.services,
                              [key]: e.target.checked
                            }
                          });
                        }}
                      />
                    }
                    label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                  />
                ))}
              </Box>

              {/* Winch Outs */}
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>Winch Outs</Typography>
              <Box sx={{ ml: 2 }}>
                {['winchOutsLightDuty', 'winchOutsMediumDuty', 'winchOutsHeavyDuty'].map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={newFacility.services?.[key] || false}
                        onChange={(e) => {
                          setNewFacility({
                            ...newFacility,
                            services: {
                              ...newFacility.services,
                              [key]: e.target.checked
                            }
                          });
                        }}
                      />
                    }
                    label={key.includes('Light') ? 'Light Duty' : key.includes('Medium') ? 'Medium Duty' : 'Heavy Duty'}
                  />
                ))}
              </Box>
            </FormGroup>
          </FormControl>

          {/* Towing Services */}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Towing Services</Typography>
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              <Box sx={{ ml: 2 }}>
                {[
                  { key: 'lightDutyTowing', label: 'Light Duty Towing' },
                  { key: 'mediumDutyTowing', label: 'Medium Duty Towing' },
                  { key: 'heavyDutyTowing', label: 'Heavy Duty Towing' },
                  { key: 'accidentSceneTowing', label: 'Accident Scene Towing' },
                  { key: 'secondaryTow', label: 'Secondary Tow' }
                ].map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={newFacility.services?.[key] || false}
                        onChange={(e) => {
                          setNewFacility({
                            ...newFacility,
                            services: {
                              ...newFacility.services,
                              [key]: e.target.checked
                            }
                          });
                        }}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            </FormGroup>
          </FormControl>

          {/* Other Services */}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Other Services</Typography>
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              <Box sx={{ ml: 2 }}>
                {[
                  { key: 'mobileMechanic', label: 'Mobile Mechanic' },
                  { key: 'mediumHeavyTire', label: 'Medium Heavy Tire' },
                  { key: 'storageFacility', label: 'Storage Facility' },
                  { key: 'keyMaking', label: 'Key Making' },
                  { key: 'locksmith', label: 'Locksmith' },
                  { key: 'electricCharging', label: 'Electric Charging' }
                ].map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={newFacility.services?.[key] || false}
                        onChange={(e) => {
                          setNewFacility({
                            ...newFacility,
                            services: {
                              ...newFacility.services,
                              [key]: e.target.checked
                            }
                          });
                        }}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            </FormGroup>
          </FormControl>
        </Box>

        {/* TODO: Add Schedule input fields if needed */}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleCreateFacility} color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Create Facility'}
        </Button>
      </DialogActions>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default CreateFacilityDialog;
